/**
 * アプリ全体の単一ソース(docs/03_architecture.md §4)。
 *
 * 画面フロー(docs/02_game_design.md §1):
 *   タイトル → モード選択 → ステージ選択 → プレイ → リザルト → リトライ / ステージ選択
 *
 * engine のイベントは `bridge/playController` が集約して `setPlaySnapshot` に流す。
 * ここから Phaser を直接触らない(演出は controller の effects 経由)。
 */

import {
  resolveModifiers,
  type Difficulty,
  type ScoreModifier,
  type SessionResult,
} from '@paltyping/engine';
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import {
  type ContentError,
  type ContentWord,
  type Language,
  type StageBundle,
  type StageCategory,
  type StageDefinition,
  type StageSetFile,
} from '../services/content';
import { buildPracticeBundle, pickPracticeWords } from '../logic/weakKeys';
import { EMPTY_CATALOG, type Catalog, type ItemSlot } from '../services/catalog';
import {
  EMPTY_INVENTORY,
  type InventoryService,
  type InventorySnapshot,
} from '../services/inventory';
import type { GachaPullResult, GachaService } from '../services/gacha';
import { TYPING_CURRENCY } from '../services/wallet';
import { appServices } from '../services/appServices';
import { onHydrated } from '../services/hydration';
import { equippedItems, equippedModifiers } from '../logic/avatar';
import {
  applyStageResult,
  bestScoreOf,
  EMPTY_PROGRESS,
  starsOf,
  type PlayLog,
  type ProgressService,
  type ProgressSnapshot,
  type StageResultReceipt,
  type StageResultSaveStatus,
  type StageResultSubmission,
} from '../services/progress';
import type { ChallengeService } from '../services/challenge';
import { displayNameError, normalizeDisplayName } from '../services/profileName';
import {
  DEFAULT_SETTINGS,
  isBirthYearMonth,
  loadSettings,
  saveSettings,
  withFirstLanguage,
  withLearningLanguageToggled,
  type AppSettings,
  type JapaneseScript,
} from '../services/settings';
import { modeOptions, type LanguageCode, type LanguageOption } from '../services/languages';
import { sound, type SoundService } from '../services/sound';
import type { PlaySnapshot } from '../bridge/playController';
import { isStageGraduated, isStageUnlocked, type StageListItem } from '../logic/unlock';
import { waitForResultTransition } from '../logic/resultTransition';
import { classifyTypingFailure } from '../services/typingFailure';

export type Screen =
  | 'title'
  | 'mode'
  | 'stageSelect'
  | 'play'
  | 'result'
  | 'gacha'
  | 'avatar'
  | 'myPage'
  /**
   * パルの ステータス(docs/14_characters.md §2)。
   * 中央にパル、まわりに領域べつの取り組み。画面のチャンクは遅延読み込み。
   */
  | 'pal'
  /** とうろく・ログイン(docs/10_paltown_platform.md §3.1)。遊ぶのに通る必要はない。 */
  | 'account'
  /**
   * 思考力パズル(docs/11_thinking_module.md §5-2)。
   * 中の画面(えらぶ→プレイ→リザルト)は `puzzleStore` が持つ。
   * この画面に入って初めてパズルのチャンクが読み込まれる。
   */
  | 'puzzle'
  /**
   * まなびモード(5教科。docs/10_paltown_platform.md §2)。
   * 中の画面(帯えらび→単元えらび→プレイ→リザルト)は `learnStore` が持つ。
   * この画面に入って初めてまなびのチャンクが読み込まれる。
   */
  | 'learn';
export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/** ミニ練習の対象キー(「もういちど」で同じ条件を組み直すために持つ)。 */
export interface PracticeSession {
  readonly weakKeys: readonly string[];
}

/**
 * 1プレイの開始時に固定する条件(docs/04 §5.1.3)。
 *
 * 読み戻しでは settings / inventory が更新されても、この値はそのプレイの
 * controller とリザルト送信で共有する。次の開始時にだけ取り直す。
 */
export interface PlayConditions {
  readonly difficulty: Difficulty;
  readonly firstLanguage: LanguageCode;
  readonly modifiers: readonly ScoreModifier[];
  readonly coinMultiplier: number;
}

/**
 * リザルト画面から見た保存状態。
 * `saving` は「画面は先に出したが、サーバーの返事はまだ」。
 */
export type ResultSaveStatus = 'saving' | StageResultSaveStatus;

export interface ResultView {
  readonly stage: StageDefinition;
  readonly difficulty: Difficulty;
  readonly result: SessionResult;
  readonly receipt: StageResultReceipt;
  /** 将来 Functions の submitStageResult に送るプレイログ要約。 */
  readonly playLog: PlayLog;
  /** きろくを保存できたか(できなかったらリザルトでやさしく知らせる)。 */
  readonly saveStatus: ResultSaveStatus;
}

export interface GameState {
  readonly screen: Screen;
  readonly language: Language;
  readonly category: StageCategory;
  readonly difficulty: Difficulty;
  readonly settings: AppSettings;
  /** なまえを送っているあいだ(連打で2回送らせない)。 */
  readonly savingDisplayName: boolean;

  readonly stages: readonly StageDefinition[];
  readonly stageStatus: LoadStatus;
  /** 開始前のサーバー照会中か。失敗時は同じ選択画面でやり直せる。 */
  readonly stageAccessStatus: LoadStatus;
  readonly stageError: string | null;

  readonly progress: ProgressSnapshot;
  readonly selectedStageId: string | null;

  readonly bundle: StageBundle | null;
  readonly bundleStatus: LoadStatus;

  readonly play: PlaySnapshot | null;
  /** 実行中プレイの開始条件。bundle と同じ開始操作で確定する。 */
  readonly playConditions: PlayConditions | null;
  readonly result: ResultView | null;
  /**
   * にがてキーのミニ練習中か(docs/07_learning_support.md §4.2)。
   * 練習中は星・コイン・進捗を保存しない。
   */
  readonly practice: PracticeSession | null;

  // ── メタゲーム(docs/02_game_design.md §7)──
  readonly catalog: Catalog;
  readonly catalogStatus: LoadStatus;
  readonly inventory: InventorySnapshot;
  /** 直近のガチャ結果(演出・結果一覧用)。画面を出るとクリアする。 */
  readonly gachaResult: GachaPullResult | null;
  readonly gachaStatus: 'idle' | 'pulling' | 'done';
  readonly gachaError: string | null;
  /**
   * いま選んでいるガチャ(docs/10 §4.3 のモジュール別ガチャ)。
   *
   * **ガチャID か 通貨キー**のどちらでもよい。通貨が普遍領域に統合された
   * (docs/13 §9.1)ことで、かずガチャとさんすうガチャは**同じ `math` 通貨の
   * 別ガチャ**になった = 通貨だけではタブを1つに決められない。
   * 画面は「ID で一致 → 通貨で一致 → 先頭」の順に解決する(`GachaScreen`)。
   */
  readonly gachaTarget: string;
  /**
   * 全通貨の残高。**`null` = まだ分からない / ローカルモード**。
   * 教科ガチャのタブは、その通貨の残高が分かるときだけ出す
   * (絶対に貯まらないコインの「0」を見せない)。
   */
  readonly walletBalances: Readonly<Record<string, number>> | null;
  /** ガチャ/アバターに入る前の画面(「もどる」の戻り先)。 */
  readonly metaReturn: Screen;
  /**
   * 「とうろく」画面をどこに合わせて開くか(docs/10 §3.4)。
   * `'transfer'` = ひきつぎカードを開いた状態で出す(旧URLの案内から来たとき)。
   */
  readonly accountFocus: 'transfer' | null;

  // ── アクション ──
  init(): Promise<void>;
  /**
   * 表示キャッシュを読み直す(docs/04 §5.1 の読み戻しのあと)。
   *
   * サーバーから読み戻すのはサービス層(`hydrateService`)の仕事で、
   * ここは「書き換わったキャッシュをもう一度読む」だけ。
   */
  refreshFromCache(): Promise<void>;
  goTitle(): void;
  goModeSelect(): void;
  chooseLanguage(language: Language): Promise<void>;
  setCategory(category: StageCategory): void;
  setDifficulty(difficulty: Difficulty): void;
  setVolume(volume: number): void;
  toggleMuted(): void;
  /** 第一言語(母語)を変える。学びたい言語から自動で除外する。 */
  setFirstLanguage(code: LanguageCode): void;
  /** 学びたい言語のチェックを入れ替える。 */
  toggleLearningLanguage(code: LanguageCode): void;
  /**
   * まなびの もじの ひょうじ(ひらがな / 漢字)を変える(docs/13)。
   *
   * 効くのは**つぎに始めるセッションから**(learnStore が組み立ての直前に読む)。
   * 遊んでいる最中に文字が入れ替わらないほうが、子どもは驚かない。
   */
  setJapaneseScript(script: JapaneseScript): void;
  /**
   * うまれた年と月(`YYYY-MM`。`null` で消す)。任意。
   * チャレンジのランキングを年齢別に分けるためだけに使う(docs/12 §1)。
   * 端末に控えを残しつつ、サーバー(`updateProfile`)にも送る。
   */
  setBirthYearMonth(value: string | null): void;
  /**
   * ランキング・リーグに出す**なまえ**を変える(docs/12 §1)。
   *
   * 生年月と違って**返事を待つ**(名前は本人が「変わった」と確かめたいもの)。
   *   - 送っているあいだは `savingDisplayName` が立ち、2回目の呼び出しは何もしない
   *   - 失敗したら**元の名前に戻して** `false` を返す(保留キューには乗せない。
   *     あとからこっそり変わるほうが分かりにくいので、その場で失敗と伝える)
   */
  saveDisplayName(value: string): Promise<boolean>;
  /**
   * 「がんばりを いろに はんえいする」の入り切り(docs/14 §2)。
   * オフ = 白いパル(既定)。
   */
  toggleEffortColor(): void;
  /**
   * 「パルの おてつだい」の入り切り(docs/07 §7.4)。
   * オフ = 詰まり検知・ヒント・応援の吹き出しを全部止める。既定はオン。
   */
  togglePalHelp(): void;
  selectStage(stageId: string | null): void;
  /** 通常ロックを無料で解除する。成功時だけ読み戻した進捗を反映する。 */
  unlockStage(stageId: string): Promise<boolean>;
  startStage(stageId: string): Promise<void>;
  retry(): Promise<void>;
  backToStageSelect(): void;
  setPlaySnapshot(snapshot: PlaySnapshot): void;
  finishSession(result: SessionResult): Promise<void>;
  resetProgress(): Promise<void>;

  /** マイページ(学習レポート。docs/07 §4.2)。 */
  goMyPage(): Promise<void>;

  /** パルの ステータス画面(docs/14 §2)。`backFromMeta` で元の画面へ戻る。 */
  goPal(): void;
  /** にがてキーだけのミニ練習を始める(進捗保存なし)。 */
  startWeakKeyPractice(weakKeys: readonly string[]): Promise<void>;

  /**
   * とうろく・ログイン画面(docs/10 §3.1)。`backFromMeta` で元の画面へ戻る。
   *
   * `focus` に `'transfer'` を渡すと、ひきつぎカード(docs/10 §3.4)を
   * 開いた状態で出す。**イベントハンドラに直接渡されることがある**ので、
   * `'transfer'` 以外は既定(何も開かない)として扱う。
   */
  goAccount(focus?: 'transfer'): void;

  /** 思考力パズル(docs/11 §5-2)。タイトルからの暫定導線。 */
  goPuzzle(): void;

  /** まなびモード(docs/10 §2)。パズルと同じくタイトルからの暫定導線。 */
  goLearn(): void;

  /**
   * ガチャ画面へ。`target`(ガチャID か 通貨キー)を渡すと、そのガチャを
   * 開いた状態で始める(まなびのリザルトから「◯◯コインで ガチャ」で来たとき)。
   */
  goGacha(target?: string): Promise<void>;
  /** ガチャ画面のタブ切り替え(タブ = ガチャ1つ)。 */
  selectGacha(gachaId: string): void;
  goAvatar(): Promise<void>;
  /** ガチャ/アバターから元の画面へ戻る。 */
  backFromMeta(): void;
  pullGacha(gachaId: string, count: number): Promise<void>;
  clearGachaResult(): void;
  equipItem(slot: ItemSlot, itemId: string | null): Promise<void>;
  setBaseCharacter(baseId: string): Promise<void>;
}

/** ストアが必要とするコンテンツ取得口(テストで差し替えられるよう構造で受ける)。 */
export interface ContentSource {
  loadStages(language: Language): Promise<StageSetFile>;
  loadStageBundle(language: Language, stageId: string): Promise<StageBundle>;
  loadCatalog(): Promise<Catalog>;
}

export interface GameStoreDeps {
  readonly content: ContentSource;
  readonly progress: ProgressService;
  readonly inventory: InventoryService;
  readonly gacha: GachaService;
  readonly sound?: Pick<
    SoundService,
    'setVolume' | 'setMuted' | 'play' | 'unlock' | 'waitVoiceEnd'
  >;
  readonly initialSettings?: AppSettings;
  readonly persistSettings?: (settings: AppSettings) => void;
  /**
   * 端末の設定を読み直す(既定は `loadSettings`)。
   * 読み戻し(docs/04 §5.1.1)でサーバーの設定が書き戻されたあとに使う。
   */
  readonly reloadSettings?: () => AppSettings;
  /**
   * ランキング用プロフィールの保存先(docs/12 §1)。
   * ローカルモードでは `NoChallengeService` = 何もしない。
   */
  readonly challenge?: Pick<ChallengeService, 'saveProfile'>;
  /**
   * セッション終了からリザルト表示までの待ち(既定 `waitForResultTransition`)。
   * テストでは即解決の関数を渡してタイマー待ちを消す。
   */
  readonly resultTransition?: () => Promise<void>;
}

/** ステージ選択画面に出す一覧(カテゴリ絞り込み+星+解放状態)。 */
export function selectStageList(state: GameState): readonly StageListItem[] {
  return state.stages
    .filter((stage) => stage.category === state.category)
    .map((stage) => ({
      stage,
      unlocked: isStageUnlocked(stage, state.progress),
      stars: starsOf(state.progress, stage.id, state.difficulty),
      bestScore: bestScoreOf(state.progress, stage.id, state.difficulty),
    }));
}

/** 装備中のアイテム(プロフィール欄・アバター画面の表示用)。 */
export function selectEquippedItems(state: GameState) {
  return equippedItems(state.catalog, state.inventory);
}

/** 装備効果。プレイ開始時に `TypingSession` の modifiers として渡す。 */
export function selectEquippedModifiers(state: GameState) {
  return equippedModifiers(state.catalog, state.inventory);
}

/**
 * 「どっちで あそぶ?」に出すモードの並び(設定駆動)。
 * 一番左上が第一言語、そのあとに学びたい言語(`services/languages` の順)。
 */
export function selectModeOptions(state: GameState): readonly LanguageOption[] {
  return modeOptions(state.settings.firstLanguage, state.settings.learningLanguages);
}

/** そのコンテンツに存在するカテゴリ(タブ表示用)。 */
export function selectCategories(state: GameState): readonly StageCategory[] {
  const found: StageCategory[] = [];
  for (const stage of state.stages) {
    if (!found.includes(stage.category)) found.push(stage.category);
  }
  return found;
}

/** ミニ練習の語を探すときに読むステージ数の上限(読み込みコストを抑える)。 */
const PRACTICE_STAGE_SCAN = 12;
/** ミニ練習の出題数。 */
const PRACTICE_WORD_COUNT = 10;

/** ミニ練習用のダミー受領証(コインも星も動かさない)。 */
const PRACTICE_RECEIPT: StageResultReceipt = {
  stars: 0,
  previousStars: 0,
  newStars: 0,
  clearCoins: 0,
  starBonusCoins: 0,
  coinsEarned: 0,
  balance: 0,
  bestScore: 0,
  isNewBestScore: false,
};

function errorMessage(error: unknown): string {
  if (error instanceof Error) return (error as ContentError).message;
  return String(error);
}

/** 同期で変わる状態を、プレイ開始時だけ読む。 */
function playConditionsOf(
  state: Pick<GameState, 'settings' | 'catalog' | 'inventory'>,
): PlayConditions {
  const modifiers = equippedModifiers(state.catalog, state.inventory).map((modifier) => ({
    ...modifier,
  }));
  return {
    difficulty: state.settings.difficulty,
    firstLanguage: state.settings.firstLanguage,
    modifiers,
    coinMultiplier: resolveModifiers(modifiers).coinMultiplier,
  };
}

export function createGameStore(deps: GameStoreDeps): UseBoundStore<StoreApi<GameState>> {
  const persist = deps.persistSettings ?? ((settings: AppSettings) => saveSettings(settings));
  const reloadSettings = deps.reloadSettings ?? (() => loadSettings());
  const audio = deps.sound;
  const initialSettings = deps.initialSettings ?? DEFAULT_SETTINGS;
  /** 古い開始前照会が、後から選んだステージを開始してしまうのを防ぐ。 */
  let startAttempt = 0;
  /** 同じステージの準備中に連打しても、照会・教材取得を重ねない。 */
  let pendingStart: { readonly attempt: number; readonly stageId: string } | null = null;

  /**
   * リザルトへ切り替えるまでの待ち。
   * 単語音声(英語モードの打ち終わり発音)の鳴り終わりまで待ってから遷移する。
   */
  const waitTransition = (): Promise<void> =>
    deps.resultTransition
      ? deps.resultTransition()
      : waitForResultTransition(audio && 'waitVoiceEnd' in audio ? audio : null);

  return create<GameState>()((set, get) => {
    const applySettings = (next: AppSettings): void => {
      const before = get().settings;
      if (before.difficulty !== next.difficulty || before.firstLanguage !== next.firstLanguage) {
        startAttempt += 1;
        // 開始前照会を取り消したら、選択画面の待機表示も必ず戻す。
        const starting = get().screen === 'play' && get().bundleStatus === 'loading';
        set({
          settings: next,
          difficulty: next.difficulty,
          stageAccessStatus: 'idle',
          ...(starting
            ? { screen: 'stageSelect', bundle: null, bundleStatus: 'idle', playConditions: null }
            : {}),
        });
      } else {
        set({ settings: next, difficulty: next.difficulty });
      }
      persist(next);
      audio?.setVolume(next.volume);
      audio?.setMuted(next.muted);
    };

    const loadCatalog = async (): Promise<Catalog> => {
      const current = get();
      if (current.catalogStatus === 'ready') return current.catalog;
      set({ catalogStatus: 'loading' });
      try {
        const catalog = await deps.content.loadCatalog();
        set({ catalog, catalogStatus: 'ready' });
        return catalog;
      } catch {
        // カタログが無くてもタイピングは遊べる。ガチャ/アバターだけ落とす。
        set({ catalogStatus: 'error' });
        return EMPTY_CATALOG;
      }
    };

    const loadStages = async (language: Language): Promise<void> => {
      set({ stageStatus: 'loading', stageError: null, stages: [] });
      try {
        const stageSet = await deps.content.loadStages(language);
        const categories = new Set(stageSet.stages.map((stage) => stage.category));
        const category: StageCategory = categories.has(get().category)
          ? get().category
          : ((stageSet.stages[0]?.category ?? 'practice') as StageCategory);
        set({ stages: stageSet.stages, stageStatus: 'ready', category });
      } catch (error) {
        set({ stageStatus: 'error', stageError: errorMessage(error) });
      }
    };

    /** 読み戻しの購読(多重購読しないようにハンドルを持つ)。 */
    let unsubscribeHydration: (() => void) | null = null;
    const refreshFromCache = async (): Promise<void> => {
      const [progress, inventory] = await Promise.all([
        deps.progress.load(),
        deps.inventory.load(),
      ]);
      // 設定もサーバーのマージ結果で書き換わっている(docs/04 §5.1.1)。
      // **保存し直さない**(書き戻したものを送り返さない)。
      const settings = reloadSettings();
      const current = get();
      const startConditionsChanged =
        current.settings.difficulty !== settings.difficulty ||
        current.settings.firstLanguage !== settings.firstLanguage;
      if (startConditionsChanged) {
        // 他端末での設定同期も、開始前照会・bundle 読込を古い条件として無効化する。
        startAttempt += 1;
      }
      // 同条件の読み戻しは準備モーダルを閉じない。プレイ中の盤面・タイマー・入力も変えず、
      // 設定は次のプレイから採用する。
      set({
        progress,
        inventory,
        settings,
        ...(get().screen === 'play' ? {} : { difficulty: settings.difficulty }),
        ...(startConditionsChanged ? { stageAccessStatus: 'idle' } : {}),
        ...(current.screen === 'play' && current.bundleStatus === 'loading'
          ? { screen: 'stageSelect', bundle: null, bundleStatus: 'idle', playConditions: null }
          : {}),
      });
      audio?.setVolume(settings.volume);
      audio?.setMuted(settings.muted);
    };

    return {
      screen: 'title',
      language: 'japanese',
      category: 'practice',
      difficulty: initialSettings.difficulty,
      settings: initialSettings,
      savingDisplayName: false,

      stages: [],
      stageStatus: 'idle',
      stageAccessStatus: 'idle',
      stageError: null,

      progress: EMPTY_PROGRESS,
      selectedStageId: null,

      bundle: null,
      bundleStatus: 'idle',

      play: null,
      playConditions: null,
      result: null,
      practice: null,

      catalog: EMPTY_CATALOG,
      catalogStatus: 'idle',
      inventory: EMPTY_INVENTORY,
      gachaResult: null,
      gachaStatus: 'idle',
      gachaError: null,
      gachaTarget: TYPING_CURRENCY,
      walletBalances: null,
      metaReturn: 'title',
      accountFocus: null,

      async init() {
        const [progress, inventory] = await Promise.all([
          deps.progress.load(),
          deps.inventory.load(),
        ]);
        set({ progress, inventory });
        // サーバーから進捗を読み戻したら(別アカウントでログイン等)、
        // 書き換わったキャッシュを読み直す(docs/04 §5.1 / docs/10 §3.1)。
        if (!unsubscribeHydration) unsubscribeHydration = onHydrated(() => void refreshFromCache());
        audio?.setVolume(get().settings.volume);
        audio?.setMuted(get().settings.muted);
        // カタログはアバター表示にも要るので起動時に先読みする(失敗しても遊べる)。
        await loadCatalog();
      },

      refreshFromCache,

      goTitle() {
        startAttempt += 1;
        set({
          screen: 'title',
          play: null,
          result: null,
          bundle: null,
          bundleStatus: 'idle',
          stageAccessStatus: 'idle',
          practice: null,
        });
      },

      goModeSelect() {
        startAttempt += 1;
        audio?.unlock();
        audio?.play('ui');
        set({ screen: 'mode', play: null, result: null, stageAccessStatus: 'idle' });
      },

      async chooseLanguage(language) {
        startAttempt += 1;
        audio?.play('ui');
        set({ screen: 'stageSelect', language, selectedStageId: null, stageAccessStatus: 'idle' });
        await loadStages(language);
      },

      setCategory(category) {
        audio?.play('ui');
        set({ category, selectedStageId: null });
      },

      setDifficulty(difficulty) {
        audio?.play('ui');
        applySettings({ ...get().settings, difficulty });
      },

      setVolume(volume) {
        applySettings({ ...get().settings, volume });
      },

      toggleMuted() {
        applySettings({ ...get().settings, muted: !get().settings.muted });
      },

      setFirstLanguage(code) {
        audio?.play('ui');
        applySettings(withFirstLanguage(get().settings, code));
      },

      toggleLearningLanguage(code) {
        audio?.play('ui');
        applySettings(withLearningLanguageToggled(get().settings, code));
      },

      setJapaneseScript(script) {
        audio?.play('ui');
        applySettings({ ...get().settings, japaneseScript: script });
      },

      toggleEffortColor() {
        audio?.play('ui');
        applySettings({
          ...get().settings,
          reflectEffortColor: !get().settings.reflectEffortColor,
        });
      },

      togglePalHelp() {
        audio?.play('ui');
        applySettings({ ...get().settings, palHelp: !get().settings.palHelp });
      },

      setBirthYearMonth(value) {
        const birthYearMonth = isBirthYearMonth(value) ? value : null;
        applySettings({ ...get().settings, birthYearMonth });
        // サーバーは権威なので送るが、**返事は待たない**(設定画面を止めない)。
        void deps.challenge?.saveProfile({ birthYearMonth });
      },

      async saveDisplayName(value) {
        // 連打・オフラインでの二重送信を防ぐ(送信中は何もしない)。
        if (get().savingDisplayName) return false;
        const displayName = normalizeDisplayName(value);
        // 画面が先に弾いている想定だが、ストアからも守る(サーバーと同じ決まり)。
        if (displayNameError(displayName) !== null) return false;

        const before = get().settings.displayName;
        if (displayName === before) return true;

        const service = deps.challenge;
        // ローカルモードには送り先が無い。控えだけ残して「できた」と言わない。
        if (!service) return false;

        // 先に見た目を変える(すぐ反映される)。だめだったら戻す。
        set({ savingDisplayName: true });
        applySettings({ ...get().settings, displayName });
        try {
          const saved = await service.saveProfile({ displayName });
          if (!saved) {
            applySettings({ ...get().settings, displayName: before });
            return false;
          }
          // サーバーが整えた値(トリム後)を正とする。
          applySettings({ ...get().settings, displayName: saved.displayName ?? displayName });
          return true;
        } catch {
          applySettings({ ...get().settings, displayName: before });
          return false;
        } finally {
          set({ savingDisplayName: false });
        }
      },

      selectStage(stageId) {
        set({ selectedStageId: stageId });
      },

      async unlockStage(stageId) {
        const current = get();
        const stage = current.stages.find((item) => item.id === stageId);
        // コンテンツに無いID・もともと開いているステージは解除APIへ送らない。
        if (
          !stage ||
          isStageGraduated(stageId, current.settings.difficulty, current.progress) ||
          isStageUnlocked(stage, current.progress)
        )
          return false;
        const unlock = deps.progress.unlockStage;
        if (!unlock) return false;
        try {
          // Firebase 実装は Functions 成功後にだけキャッシュを更新する。
          const progress = await unlock.call(deps.progress, stageId, current.settings.difficulty);
          set({ progress });
          return true;
        } catch {
          // 失敗時に見た目だけ解除しない。再試行は UI が案内する。
          return false;
        }
      },

      async startStage(stageId) {
        // 同じ条件・同じステージの連打は、教材がそろうまで同じ開始をやり直さない。
        if (pendingStart?.attempt === startAttempt && pendingStart.stageId === stageId) return;

        const attempt = ++startAttempt;
        const beforeAccess = get();
        const accessDifficulty = beforeAccess.settings.difficulty;
        const accessLanguage = beforeAccess.language;
        const accessFirstLanguage = beforeAccess.settings.firstLanguage;
        const accessScreen = beforeAccess.screen;

        // Safari / モバイルでは最初の await 後だとユーザー操作由来と見なされず、
        // AudioContext を再開できないことがある。解除だけは同期区間で済ませる。
        audio?.unlock();

        // 卒業はデバッグ解放・手動解除より先に止める。
        if (isStageGraduated(stageId, accessDifficulty, beforeAccess.progress)) return;

        const refreshAccess = deps.progress.refreshStageAccess;
        set({ stageAccessStatus: 'loading', stageError: null });
        if (!refreshAccess) {
          set({
            stageAccessStatus: 'error',
            stageError: classifyTypingFailure({ code: 'recovery-required' }).message,
          });
          return;
        }

        pendingStart = { attempt, stageId };
        try {
          // 公開教材はアカウントに依存しない。照会と同時に開始するが、拒否・遷移時は
          // この値を破棄する。reject を結果型へ畳むので、照会側が先に終わっても未処理にしない。
          const bundleResult = (() => {
            try {
              return deps.content.loadStageBundle(accessLanguage, stageId).then(
                (bundle) => ({ ok: true as const, bundle }),
                (error: unknown) => ({ ok: false as const, error }),
              );
            } catch (error) {
              return Promise.resolve({ ok: false as const, error });
            }
          })();

          let progress: ProgressSnapshot;
          try {
            // pending の結果もサービスが flush し終えるまで待ってから照会する。
            progress = await refreshAccess.call(deps.progress, stageId, accessDifficulty);
          } catch (error) {
            if (attempt === startAttempt && get().screen === accessScreen) {
              set({
                stageAccessStatus: 'error',
                stageError: classifyTypingFailure(error).message,
              });
            }
            return;
          }
          const afterAccess = get();
          // 照会中に画面遷移・別開始・難易度/言語・母語変更があった応答は使わない。
          if (
            attempt !== startAttempt ||
            afterAccess.screen !== accessScreen ||
            afterAccess.language !== accessLanguage ||
            afterAccess.settings.difficulty !== accessDifficulty ||
            afterAccess.settings.firstLanguage !== accessFirstLanguage
          ) {
            if (attempt === startAttempt) set({ stageAccessStatus: 'idle' });
            return;
          }
          // 教材もそろうまで準備表示を維持する。進捗だけはサーバー照会の正本を採用する。
          set({ progress, stageError: null });

          const current = get();
          const stage = current.stages.find((item) => item.id === stageId);
          // カード以外の入口からも卒業・通常ロックを通り抜けられないようにする。
          if (
            stage &&
            (isStageGraduated(stageId, current.settings.difficulty, current.progress) ||
              !isStageUnlocked(stage, current.progress))
          ) {
            set({ stageAccessStatus: 'idle' });
            return;
          }

          const prepared = await bundleResult;
          const loaded = get();
          // 教材待ち中に別画面へ移動していたら、成功・失敗のどちらも破棄する。
          if (
            attempt !== startAttempt ||
            loaded.screen !== accessScreen ||
            loaded.language !== accessLanguage ||
            loaded.settings.difficulty !== accessDifficulty ||
            loaded.settings.firstLanguage !== accessFirstLanguage
          ) {
            if (attempt === startAttempt && loaded.screen === accessScreen) {
              set({ stageAccessStatus: 'idle' });
            }
            return;
          }
          if (!prepared.ok) {
            set({ stageAccessStatus: 'error', stageError: errorMessage(prepared.error) });
            return;
          }

          // 一覧を読んでいない直接開始でも、定義を得た時点で通常ロックを必ず再判定する。
          const started = get();
          if (
            isStageGraduated(stageId, accessDifficulty, started.progress) ||
            !isStageUnlocked(prepared.bundle.stage, started.progress)
          ) {
            set({ stageAccessStatus: 'idle' });
            return;
          }
          const conditions = playConditionsOf(started);
          audio?.play('ui');
          set({
            screen: 'play',
            selectedStageId: stageId,
            bundle: prepared.bundle,
            bundleStatus: 'ready',
            play: null,
            playConditions: conditions,
            result: null,
            practice: null,
            stageAccessStatus: 'idle',
            // 同期中に変わった難易度も、次のプレイから画面表示とそろえる。
            difficulty: conditions.difficulty,
          });
        } finally {
          if (pendingStart?.attempt === attempt) pendingStart = null;
        }
      },

      async retry() {
        // 保存中の「もういちど」は、3回目の結果が確定する前に4回目を始め得る。
        // リザルト画面だけでなく直接呼び出しもここで止める。
        if (get().result?.saveStatus === 'saving') return;
        const practice = get().practice;
        if (practice) {
          await get().startWeakKeyPractice(practice.weakKeys);
          return;
        }
        const stageId = get().selectedStageId;
        if (!stageId) return;
        await get().startStage(stageId);
      },

      backToStageSelect() {
        startAttempt += 1;
        audio?.play('ui');
        // ミニ練習から抜けるときはマイページに戻す(そこから来ているため)。
        set({
          screen: get().practice ? 'myPage' : 'stageSelect',
          play: null,
          result: null,
          bundle: null,
          bundleStatus: 'idle',
          stageAccessStatus: 'idle',
          practice: null,
        });
      },

      setPlaySnapshot(snapshot) {
        set({ play: snapshot });
      },

      /**
       * セッション終了 → リザルト表示。
       *
       * 待ち時間の設計(docs/02 §1 の画面フロー):
       * サーバー送信と演出の待ちを**並行**に走らせ、演出の待ち
       * (`waitForResultTransition` = max(1秒, 単語音声の鳴り終わり) 上限4秒)が
       * 明けた時点で画面を切り替える。送信が間に合わなければ engine の計算値で
       * 先に出し、返事が来たら受領証だけ差し替える。
       * (以前は送信 → 進捗読み込み → 表示の**直列**だったので、
       *  Functions のコールドスタートぶんだけ「クリア表示までが長い」状態だった)
       */
      async finishSession(result) {
        const state = get();
        const stage = state.bundle?.stage;
        if (!stage) return;
        const playLog: PlayLog = {
          totalTyped: result.correctKeys,
          totalMiss: result.missKeys,
          durationMs: Math.round(result.durationMs),
          wordsCompleted: result.completedQuestions,
        };
        const view = (receipt: StageResultReceipt, saveStatus: ResultSaveStatus): ResultView => ({
          stage,
          difficulty: result.difficulty,
          result,
          receipt,
          playLog,
          saveStatus,
        });
        const transition = waitTransition();

        /** 待っている間に「やめる」等で画面を離れていないか。 */
        const stillOnPlay = (): boolean => get().screen === 'play';

        // ミニ練習は進捗・コインを一切動かさない(学習イベントの記録だけ残る)。
        if (state.practice) {
          await transition;
          if (!stillOnPlay()) return;
          set({ screen: 'result', result: view(PRACTICE_RECEIPT, 'saved') });
          audio?.play(result.cleared ? 'clear' : 'miss');
          return;
        }

        const submission: StageResultSubmission = {
          stageId: stage.id,
          difficulty: result.difficulty,
          // クリアコインの係数は言語別(docs/02 §5.7)。ステージ定義の言語をそのまま使う。
          language: stage.language,
          score: Math.floor(result.score),
          cleared: result.cleared,
          starCoins: stage.starCoins,
          // 終了時点の装備ではなく、controller に渡した開始時の条件で計算する。
          coinMultiplier: state.playConditions?.coinMultiplier ?? 1,
          // ローカルデモだけが使う。Firebase callable にはサービス層で送らない。
          firstLanguage: state.playConditions?.firstLanguage ?? state.settings.firstLanguage,
          playLog,
        };
        // 送信は待たずに走らせる(演出の待ちと重ねる)。
        const answered = { value: false };
        const submitted = deps.progress
          .submitStageResult(submission)
          .catch((error: unknown) => {
            // サービス実装は基本ここまで例外を上げない(オフラインは内部で保留にする)。
            console.warn('[paltyping] リザルトを保存できませんでした', error);
            return null;
          })
          .then((receipt) => {
            answered.value = true;
            return receipt;
          });

        await transition;
        if (!stillOnPlay()) {
          // 画面を離れた。送信だけ見届けて進捗を反映し、リザルトは割り込ませない。
          await submitted;
          set({ progress: await deps.progress.load() });
          return;
        }
        let shownEarly = false;
        if (!answered.value) {
          // まだ返事が無い。engine の計算値で先に画面を出す(子どもを待たせない)。
          set({
            screen: 'result',
            result: view(
              applyStageResult(state.progress, submission, Date.now()).receipt,
              'saving',
            ),
          });
          audio?.play(result.cleared ? 'clear' : 'miss');
          shownEarly = true;
        }

        const settled = await submitted;
        const progress = await deps.progress.load();
        // 待っている間にリザルトを離れていたら、進捗だけ反映して上書きしない。
        if (shownEarly && get().screen !== 'result') {
          set({ progress });
          return;
        }
        const receipt = settled ?? applyStageResult(progress, submission, Date.now()).receipt;
        const saveStatus: ResultSaveStatus = settled ? (settled.saveStatus ?? 'saved') : 'rejected';
        set({ screen: 'result', result: view(receipt, saveStatus), progress });
        if (!shownEarly) audio?.play(result.cleared ? 'clear' : 'miss');
      },

      async resetProgress() {
        const [progress, inventory] = await Promise.all([
          deps.progress.reset(),
          deps.inventory.reset(),
        ]);
        set({ progress, inventory, gachaResult: null, gachaStatus: 'idle' });
      },

      async goMyPage() {
        audio?.play('ui');
        const from = get().screen;
        set({
          screen: 'myPage',
          play: null,
          result: null,
          practice: null,
          ...(from === 'myPage' ? {} : { metaReturn: from }),
        });
        // タイトルから直接来た場合はステージ未読み込み(ミニ練習の語源に要る)。
        if (get().stages.length === 0) await loadStages(get().language);
      },

      async startWeakKeyPractice(weakKeys) {
        if (weakKeys.length === 0) return;
        audio?.unlock();
        audio?.play('ui');
        const language = get().language;
        set({
          screen: 'play',
          practice: { weakKeys },
          selectedStageId: null,
          bundle: null,
          bundleStatus: 'loading',
          play: null,
          playConditions: null,
          result: null,
        });
        try {
          if (get().stages.length === 0) await loadStages(language);
          const state = get();
          const candidates = state.stages
            .filter((stage) => isStageUnlocked(stage, state.progress))
            .slice(0, PRACTICE_STAGE_SCAN);
          const bundles = await Promise.all(
            candidates.map((stage) =>
              deps.content.loadStageBundle(language, stage.id).catch(() => null),
            ),
          );
          const pool = new Map<string, ContentWord>();
          for (const bundle of bundles) {
            if (!bundle) continue;
            for (const [id, word] of bundle.wordsById) pool.set(id, word);
          }
          const words = pickPracticeWords(
            [...pool.values()],
            weakKeys,
            language,
            PRACTICE_WORD_COUNT,
          );
          // 練習をやめて別画面に移っていたら破棄する。
          if (get().screen !== 'play' || !get().practice) return;
          if (words.length === 0) {
            set({ screen: 'myPage', practice: null, bundleStatus: 'idle' });
            return;
          }
          const conditions = playConditionsOf(get());
          set({
            bundle: buildPracticeBundle(language, words),
            bundleStatus: 'ready',
            playConditions: conditions,
            difficulty: conditions.difficulty,
          });
        } catch (error) {
          set({
            screen: 'myPage',
            practice: null,
            bundleStatus: 'error',
            stageError: errorMessage(error),
          });
        }
      },

      goPal() {
        audio?.play('ui');
        const from = get().screen;
        set({
          screen: 'pal',
          play: null,
          result: null,
          practice: null,
          ...(from === 'pal' ? {} : { metaReturn: from }),
        });
      },

      goAccount(focus) {
        audio?.play('ui');
        const from = get().screen;
        set({
          screen: 'account',
          // onClick から直接渡されたイベントを取り違えない(値で厳密に見る)。
          accountFocus: focus === 'transfer' ? 'transfer' : null,
          ...(from === 'account' ? {} : { metaReturn: from }),
        });
      },

      goPuzzle() {
        audio?.unlock();
        audio?.play('ui');
        set({ screen: 'puzzle', play: null, result: null, practice: null });
      },

      goLearn() {
        audio?.unlock();
        audio?.play('ui');
        set({ screen: 'learn', play: null, result: null, practice: null });
      },

      async goGacha(target) {
        audio?.unlock();
        audio?.play('ui');
        const from = get().screen;
        set({
          screen: 'gacha',
          gachaResult: null,
          gachaStatus: 'idle',
          gachaError: null,
          ...(target === undefined ? {} : { gachaTarget: target }),
          ...(from === 'gacha' || from === 'avatar' ? {} : { metaReturn: from }),
        });
        await loadCatalog();
        // 教科コインの残高はサーバーだけが知っている(CLAUDE.md ルール2)。
        // 取れなくても画面は出す(タイピングのコインは進捗に載っている)。
        const balances = await deps.gacha.balances();
        if (balances && get().screen === 'gacha') set({ walletBalances: balances });
      },

      selectGacha(gachaId) {
        audio?.play('ui');
        set({
          gachaTarget: gachaId,
          gachaResult: null,
          gachaStatus: 'idle',
          gachaError: null,
        });
      },

      async goAvatar() {
        audio?.play('ui');
        const from = get().screen;
        set({
          screen: 'avatar',
          ...(from === 'gacha' || from === 'avatar' ? {} : { metaReturn: from }),
        });
        await loadCatalog();
      },

      backFromMeta() {
        audio?.play('ui');
        set({ screen: get().metaReturn, gachaResult: null, gachaStatus: 'idle', gachaError: null });
      },

      async pullGacha(gachaId, count) {
        if (get().gachaStatus === 'pulling') return;
        set({ gachaStatus: 'pulling', gachaError: null, gachaResult: null });
        try {
          const result = await deps.gacha.pull({ gachaId, count });
          const progress = await deps.progress.load();
          // 抽選後の残高はサーバーが返す。教科コインもここで一緒に更新する。
          const balances = result.balances ?? {
            ...(get().walletBalances ?? {}),
            [result.currency]: result.balance,
          };
          set({
            gachaResult: result,
            gachaStatus: 'done',
            inventory: result.inventory,
            progress,
            ...(get().walletBalances === null && result.balances === undefined
              ? {}
              : { walletBalances: balances }),
          });
          audio?.play('clear');
        } catch (error) {
          set({ gachaStatus: 'idle', gachaError: errorMessage(error) });
        }
      },

      clearGachaResult() {
        set({ gachaResult: null, gachaStatus: 'idle', gachaError: null });
      },

      async equipItem(slot, itemId) {
        audio?.play('ui');
        set({ inventory: await deps.inventory.equip(slot, itemId) });
      },

      async setBaseCharacter(baseId) {
        audio?.play('ui');
        set({ inventory: await deps.inventory.setBase(baseId) });
      },
    };
  });
}

/**
 * 本番用のストア。
 *
 * ローカルモード / Firebase モードの選択は `services/factory.ts` が env から決める
 * (`VITE_FIREBASE_*` が揃っていれば callable 経由、無ければ localStorage)。
 */
const services = appServices;

export const useGameStore = createGameStore({
  content: services.content,
  progress: services.progress,
  inventory: services.inventory,
  gacha: services.gacha,
  sound,
  initialSettings: loadSettings(),
  challenge: services.challenge,
});
